import React, {useState} from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import {useSession} from '../firebase/UserProvider';
import {auth} from '../firebase/config';
import {createOrGetOneOnOneChat, extractUid} from '../firebase/firestore/chats';
import useChatStore from '../stores/useChatStore';

/**
 * Reusable SendMessageButton
 * Props:
 * - user: target user object (must contain uid, id, or userId)
 * - size, variant: forwarded to MUI Button
 * - children: button label (defaults to 'Send message')
 * - onOpened(chat): optional callback invoked when chat is ready
 * - parentId: optional owner id; tagged onto newly opened hub windows
 */
const SendMessageButton = ({user, size = 'small', variant = 'contained', children, onOpened, isIcon = false, parentId}) => {
    const [creating, setCreating] = useState(false);
    const {user: sessionUser} = useSession() || {};
    const currentUser = sessionUser || auth.currentUser || null;

    const handleClick = async (event) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();
        if (creating) return;
        const targetId = extractUid(user);
        const myId = extractUid(currentUser);
        if (!targetId || !myId) {
            console.warn('Cannot start chat - missing participant ids', {targetId, myId});
            return;
        }
        if (targetId === myId) {
            console.warn('Cannot start chat - target is the current user', {targetId});
            return;
        }

        try {
            setCreating(true);
            const chat = await createOrGetOneOnOneChat(user, currentUser);
            if (chat?.id) {
                // Read the store at call time so a parent-modal unmount cannot drop the open.
                useChatStore.getState().openChatInHub(
                    chat,
                    user,
                    parentId ? {parentId} : undefined,
                );
                if (typeof onOpened === 'function') onOpened(chat);
            }
        } catch (err) {
            console.error('Error creating chat', err);
        } finally {
            setCreating(false);
        }
    };

    const targetId = extractUid(user);
    const myId = extractUid(currentUser);
    const disabled = creating || !targetId || !myId || targetId === myId;

    // Render an IconButton when isIcon is true, otherwise render the normal Button.
    if (isIcon) {
        const label = (children || 'Send message');
        return (
            <Tooltip title={label} arrow>
                <span>
                    <IconButton
                        size={size}
                        onClick={handleClick}
                        disabled={disabled}
                        aria-label={label}
                    >
                        {creating ? <CircularProgress size={16}/> : <ChatBubbleOutlineRoundedIcon/>}
                    </IconButton>
                </span>
            </Tooltip>
        );
    }

    return (
        <Button
            variant={variant}
            size={size}
            onClick={handleClick}
            disabled={disabled}
            startIcon={<ChatBubbleOutlineRoundedIcon/>}
        >
            {creating ? 'Opening...' : (children || 'Send message')}
        </Button>
    );
};

export default SendMessageButton;
